import { ApiClient } from '../client/ApiClient';
import {
  AttachCategorySuggestionsQuery,
  AttachCategorySuggestionsResult,
  AdsQuery,
  CollectionQuery,
  EntriesQuery,
  QueryType,
} from './types';
import { ContentfulCampaign, ContentfulCollection as JbContentfulCollection } from '../contentful/types';
import { ApiResponse } from '../types';
import { ContentfulCollection } from 'contentful';
import resolveResponse from 'contentful-resolve-response';

export interface BasicEntryCollection<T> extends Omit<ContentfulCollection<T>, 'toPlainObject'> {
  errors?: Array<any>;
  includes?: any;
}

export class ContentfulThemeApiClient extends ApiClient {
  private readonly _themeApiBaseUrl: string;

  constructor({ themeApiBaseUrl }: { themeApiBaseUrl?: string }) {
    super();
    this._themeApiBaseUrl = `${themeApiBaseUrl}/content`;
  }

  _fetchUnresolvedEntries = async <
    Result = BasicEntryCollection<any>,
    Options extends Record<string, any> = Record<string, any>,
  >(
    type: QueryType,
    options?: Options,
    config?: EntriesQuery['config']
  ): Promise<ApiResponse<Result>> => {
    const shouldUsePreviewSpace = window.Jbhifi?.isContentPreview?.toString() || 'false';
    let release: string | undefined;
    if (shouldUsePreviewSpace) {
      const isHelpAndSupport = window.location.pathname.includes('/pages/help-and-support/');
      const params = new URLSearchParams(window.location.search);
      release = isHelpAndSupport ? window.Jbhifi.contentfulReleaseTimeline : params.get('release') || undefined;
    }
    const optionsQueryString = options
      ? new URLSearchParams({
          ...options,
          shouldUsePreviewSpace,
          ...(release ? { 'release[lte]': release } : {}),
        }).toString()
      : '';

    const { data, statusCode, error } = await this._fetch<any>({
      path: `${this._themeApiBaseUrl}/${type}?${optionsQueryString}`,
      method: 'GET',
    });
    if (error) {
      return { error, statusCode };
    }

    const items = resolveResponse(data.data, {
      removeUnresolved: config?.shouldRemoveUnresolved || false,
    });

    return {
      data: { ...data.data, items },
      error: error,
      statusCode,
    };
  };

  getCollection = async (
    options: CollectionQuery['options']
  ): Promise<ApiResponse<BasicEntryCollection<JbContentfulCollection>>> => {
    return await this._fetchUnresolvedEntries('entries', {
      content_type: 'smartCollection',
      'fields.handle': options.handle,
      include: options?.include || '4',
      ...(options.release ? { 'release[lte]': options.release } : {}),
    });
  };

  getCampaigns = async (
    options: AdsQuery['options'],
    isPagefly?: boolean
  ): Promise<ApiResponse<BasicEntryCollection<ContentfulCampaign>>> => {
    const queryOptions = {
      content_type: isPagefly ? 'pageCampaign' : 'newCampaign',
      'fields.campaignRulesets.sys.contentType.sys.id': isPagefly ? 'pageRuleset' : 'campaignRulesBackup',
      'fields.campaignRulesets.fields.handles': options.handle,
      include: options?.include || '6',
    };
    return await this._fetchUnresolvedEntries('entries', queryOptions);
  };

  getAttachCategorySuggestions = async (options: AttachCategorySuggestionsQuery['options']) => {
    const result = await this._fetchUnresolvedEntries<AttachCategorySuggestionsResult>(
      'attachCategorySuggestions',
      options
    );
    return {
      ...result,
      data: result?.data?.data,
    };
  };

  getEntries = async <T>(
    queryOptions: EntriesQuery['queryOptions'],
    config?: EntriesQuery['config']
  ): Promise<ApiResponse<BasicEntryCollection<T>>> => {
    return await this._fetchUnresolvedEntries('entries', queryOptions, config);
  };
}
